import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import sessionStorageModule from 'redux-persist/lib/storage/session';
import appReducer from './slices/appSlice';
import conversationReducer from './slices/conversationSlice';
import projectReducer from './slices/projectSlice';
import prototyperReducer from './slices/prototyperSlice';
import { isWebBuild } from '../utils/environment';

// Create a noop storage for plugin builds (Figma plugins don't have localStorage/sessionStorage)
const createNoopStorage = () => {
  return {
    getItem(_key: string): Promise<null> {
      return Promise.resolve(null);
    },
    setItem(_key: string, _value: any): Promise<any> {
      return Promise.resolve(_value);
    },
    removeItem(_key: string): Promise<void> {
      return Promise.resolve();
    },
  };
};

// Get the appropriate storage based on build target
// Use noop storage for plugin builds to avoid sync storage warnings
const getStorage = () => {
  if (isWebBuild()) {
    // For web builds, try to use sessionStorage, fallback to noop if unavailable
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        return sessionStorageModule;
      }
    } catch (e) {
      console.warn('sessionStorage not available, using noop storage');
    }
  }
  // For plugin builds or when sessionStorage is unavailable, use noop storage
  return createNoopStorage();
};

// Combine all reducers
const combinedReducer = combineReducers({
  app: appReducer,
  conversation: conversationReducer,
  project: projectReducer,
  prototyper: prototyperReducer,
});

// Root reducer that handles global state reset on logout
const rootReducer = (state: any, action: any) => {
  // Reset all state to initial values when logout action is dispatched
  if (action.type === 'app/logout') {
    console.log('🔄 Redux state reset: Clearing all state due to logout');
    
    // Clear ALL localStorage items in web builds
    if (isWebBuild()) {
      try {
        // Clear all localStorage items
        localStorage.clear();
        // Clear all sessionStorage items
        window.sessionStorage.clear();
        console.log('🧹 Cleared ALL localStorage and sessionStorage items');
      } catch (e) {
        console.warn('Could not clear storage:', e);
      }
    }
    
    // Return undefined to let reducers initialize with their initial state
    state = undefined;
  }
  
  return combinedReducer(state, action);
};

// Configure persistence - use noop storage for plugin builds
const persistConfig = {
  key: 'ideoz-app-state',
  version: 1,
  storage: getStorage(),
  whitelist: ['app', 'conversation', 'project', 'prototyper'], // Persist all slices
  // Reset loading states on rehydration
  stateReconciler: (inboundState: any, originalState: any) => {
    console.log('🔄 Rehydrating state from sessionStorage');
    return {
      ...inboundState,
      // Force app loading states to their correct initial values
      app: inboundState.app ? {
        ...inboundState.app,
        isAppLoading: true // Always start with true on rehydration
      } : { ...originalState.app, isAppLoading: true },
      conversation: inboundState.conversation ? {
        ...inboundState.conversation,
        isLoading: false, // Reset loading state on page refresh
        // Migrate old 'DeepSeek' values to 'Gemini'
        llmFamily: inboundState.conversation.llmFamily === 'DeepSeek' 
          ? 'Gemini' 
          : (inboundState.conversation.llmFamily || 'Gemini')
      } : originalState.conversation
    };
  },
  // Debug configuration
  debug: process.env.NODE_ENV === 'development'
} as any;

export const createAppStore = () => {
  // Conditionally apply persistence based on build target
  const reducer: any = isWebBuild() 
    ? persistReducer(persistConfig, rootReducer)
    : rootReducer;

  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
          ignoredActionsPaths: ['payload.timestamp'],
          ignoredPaths: ['conversation.messages.timestamp'],
        },
      }),
  });

  return store;
};

export type AppStore = ReturnType<typeof createAppStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

// Re-export hooks
export { useAppDispatch, useAppSelector } from './hooks';

// Create persistor (will be null for plugin builds)
let persistor: any = null;

export const getPersistor = (store: AppStore) => {
  if (isWebBuild() && !persistor) {
    persistor = persistStore(store);
    
    // Add logging for web builds in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Redux Persist configured with sessionStorage');
      console.log('📦 Persisting state:', ['app', 'conversation', 'project']);
      console.log('🚫 isAppLoading will always start as TRUE on rehydration');
      
      persistor.subscribe(() => {
        console.log('💾 State persisted to sessionStorage');
      });
    }
  }
  return persistor;
};

// Utility function to manually clear persisted state (web environment only)
export const clearPersistedState = () => {
  if (isWebBuild()) {
    try {
      window.sessionStorage.removeItem('persist:ideoz-app-state');
      console.log('🧹 Cleared persisted state from sessionStorage');
      return true;
    } catch (error) {
      console.error('Failed to clear persisted state:', error);
      return false;
    }
  }
  return false;
};

// Utility function to check if persisted state exists
export const hasPersistedState = (): boolean => {
  if (isWebBuild()) {
    try {
      const persistedState = window.sessionStorage.getItem('persist:ideoz-app-state');
      return persistedState !== null;
    } catch (error) {
      console.error('Failed to check persisted state:', error);
      return false;
    }
  }
  return false;
};

// Re-export prototyper slice actions and selectors
export * from './slices/prototyperSlice';
